// @ts-check
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';

import { useEnvelopeProgress } from '@/composables/envelopes';
import { useFlowRouteDetails, useStepRoutes } from '@/composables/flows';

export function useEnvelopesRoutes() {
  const currentRoute = useRoute();

  const { stepsRoutes, stepsHash } = useStepRoutes('EnvelopesSteps');
  const {
    isEditRoute, isEditStepRoute, isStepsRoute, isCreateRoute,
  } = useFlowRouteDetails(currentRoute);

  const currentIndex = computed(() =>
    stepsRoutes.findIndex(route => route.name === currentRoute.name));
  const summaryIndex = stepsRoutes.findIndex(route => route.name === 'EnvelopesStepSummary');

  const progressSteps = {
    documents: stepsRoutes.find(route => route.name === 'EnvelopesStepDocuments'),
    parties: stepsRoutes.find(route => route.name === 'EnvelopesStepParties'),
    fields: stepsRoutes.find(route => route.name === 'EnvelopesStepFields'),
    'add-fields': stepsRoutes.find(route => route.name === 'EnvelopesStepAddFields'),
    summary: stepsRoutes.find(route => route.name === 'EnvelopesStepSummary'),
  };

  const prevRoute = computed(() => {
    if (isEditRoute.value || isEditStepRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value - 1];
      return route && { name: route.name };
    }

    return { name: 'Envelopes' };
  });

  const nextRoute = computed(() => {
    if (isCreateRoute.value) {
      return stepsRoutes[1];
    }

    if (isEditRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value + 1];
      return route && { name: route.name };
    }

    return undefined;
  });

  return {
    currentRoute,
    currentIndex,
    summaryIndex,
    progressSteps,
    isStepsRoute,
    isEditRoute,
    isCreateRoute,
    isEditStepRoute,
    prevRoute,
    nextRoute,
    stepsHash,
    stepsRoutes,
  };
}

/**
 * Progress update to POST
 */
export function useProgressUpdate() {
  const {
    isCreateRoute,
    isEditRoute,
    currentIndex,
    stepsHash,
    summaryIndex,
  } = useEnvelopesRoutes();

  return computed(() => {
    if (isCreateRoute.value) {
      return undefined;
    }

    let index = currentIndex.value >= 0 ? currentIndex.value : 0;
    if (isEditRoute.value) {
      index = summaryIndex;
    }

    return {
      hash: stepsHash,
      index,
    };
  });
}

/**
 * Progress route
 * redirect to this route to ensure all steps are progressed through in order
 */
export function useProgressRoute() {
  const {
    currentRoute,
    currentIndex,
    summaryIndex,
    stepsHash,
    stepsRoutes,
    isEditRoute,
  } = useEnvelopesRoutes();

  const fingerprint = computed(() => currentRoute.params.fingerprint);
  const { envelopeProgress, envelopeSent } = useEnvelopeProgress(fingerprint);

  return computed(() => {
    const { hash, index } = envelopeProgress.value;
    let progressIndex = typeof index === 'number' ? index + 1 : 1;
    if (index === summaryIndex) {
      progressIndex = summaryIndex;
    }

    // no step in url go to next incomplete step
    // step in url is before go to it instead
    const nextIndex =
      currentIndex.value < 0
        ? progressIndex
        : Math.min(currentIndex.value, progressIndex);

    // envelope has been sent so summary only
    if (envelopeSent.value) {
      return stepsRoutes[summaryIndex];
    }

    // hashes don't match, steps have changed so go to beginning
    if (hash && hash !== stepsHash) {
      return stepsRoutes[0];
    }

    // allow edit routes after reaching summary
    if (isEditRoute.value && progressIndex >= summaryIndex) {
      return currentRoute;
    }

    return stepsRoutes[nextIndex];
  });
}
